import { cloneElement, forwardRef, isValidElement, useId } from 'react';
import { cn } from './cn.js';

const control =
  'block w-full rounded-lg border-0 bg-white px-3 text-slate-900 shadow-xs ring-1 ring-inset ring-slate-300 ' +
  'placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-brand-600 disabled:bg-slate-50 disabled:text-slate-500 ' +
  'aria-[invalid=true]:ring-red-400';

export const Input = forwardRef(function Input({ className, size = 'md', ...props }, ref) {
  return (
    <input ref={ref} className={cn(control, size === 'lg' ? 'h-12 text-base' : 'h-10 text-sm', className)} {...props} />
  );
});

export const Textarea = forwardRef(function Textarea({ className, rows = 3, ...props }, ref) {
  return <textarea ref={ref} rows={rows} className={cn(control, 'py-2 text-sm', className)} {...props} />;
});

export const Select = forwardRef(function Select({ className, children, ...props }, ref) {
  return (
    <select ref={ref} className={cn(control, 'h-10 pr-8 text-sm', className)} {...props}>
      {children}
    </select>
  );
});

export const Checkbox = forwardRef(function Checkbox({ label, description, className, ...props }, ref) {
  const id = useId();
  return (
    <label htmlFor={props.id ?? id} className={cn('flex cursor-pointer items-start gap-2.5', className)}>
      <input
        ref={ref}
        id={props.id ?? id}
        type="checkbox"
        className="mt-0.5 size-4 rounded border-slate-300 text-brand-600 accent-brand-600 focus:ring-brand-600"
        {...props}
      />
      <span className="text-sm">
        <span className="font-medium text-slate-800">{label}</span>
        {description && <span className="block text-slate-500">{description}</span>}
      </span>
    </label>
  );
});

export function Switch({ checked, onChange, label, description, disabled }) {
  return (
    <label className={cn('flex items-center justify-between gap-4', disabled ? 'opacity-60' : 'cursor-pointer')}>
      <span className="text-sm">
        <span className="font-medium text-slate-800">{label}</span>
        {description && <span className="block text-slate-500">{description}</span>}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={Boolean(checked)}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cn(
          'relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600',
          checked ? 'bg-brand-600' : 'bg-slate-300',
        )}
      >
        <span
          className={cn(
            'inline-block size-5 translate-y-0.5 rounded-full bg-white shadow transition-transform',
            checked ? 'translate-x-5.5' : 'translate-x-0.5',
          )}
        />
      </button>
    </label>
  );
}

const CONTROL_TYPES = new Set([Input, Textarea, Select, 'input', 'select', 'textarea']);

/**
 * Label + control + hint/error. Pass the control as children.
 * A single Input/Select/Textarea child is wired up automatically: the label points at it,
 * and its hint/error are announced via aria-describedby / aria-invalid.
 */
export function Field({ label, error, hint, required, className, children, htmlFor }) {
  const autoId = useId();
  const isControl = isValidElement(children) && CONTROL_TYPES.has(children.type);
  const id = htmlFor ?? (isControl ? (children.props.id ?? autoId) : undefined);
  const noteId = `${autoId}-note`;
  const control = isControl
    ? cloneElement(children, {
        id,
        'aria-invalid': error ? true : undefined,
        'aria-required': required || undefined,
        'aria-describedby': error || hint ? noteId : undefined,
      })
    : children;
  return (
    <div className={cn('space-y-1.5', className)}>
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-slate-700">
          {label}
          {required && (
            <span className="text-red-500" aria-hidden>
              {' '}
              *
            </span>
          )}
        </label>
      )}
      {control}
      {error ? (
        <p id={noteId} className="text-xs text-red-600" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p id={noteId} className="text-xs text-slate-500">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

/** Read a nested react-hook-form error message by dotted path. */
export function errorAt(errors, path) {
  return path.split('.').reduce((acc, k) => acc?.[k], errors)?.message;
}

/** Copy server field errors (ApiError.fields) onto a react-hook-form instance. */
export function applyServerErrors(setError, apiError) {
  for (const [path, message] of Object.entries(apiError?.fields ?? {})) {
    setError(path, { type: 'server', message });
  }
}
